const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Check if we should use mock API (when no backend URL is set)
const USE_MOCK_API = false; // Disable mock API for production

const UPLOAD_LIMIT_MESSAGE = 'لا يمكن رفع أكثر من 80 ميغابايت في المرة الواحدة';
const SESSION_EXPIRED_MESSAGE = 'انتهت صلاحية الجلسة، يرجى تسجيل الدخول مرة أخرى';

const CONNECTION_ERROR_MESSAGE = 'تعذر الاتصال بالخادم. حاول مرة أخرى';

const isPayloadTooLarge = (status, text = '') => (
  status === 413
  || /FUNCTION_PAYLOAD_TOO_LARGE|Request Entity Too Large|payload too large/i.test(text)
);

const isSessionError = (status, text = '') => (
  (status === 401 || status === 403)
  && /token|expired|unauthorized|unauthorised|access token/i.test(text)
);

const redirectToLogin = () => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('adminToken');
  const path = window.location.pathname || '';
  if (!path.includes('/admin/login')) {
    window.location.assign('/admin/login');
  }
};

const asApiError = (error) => {
  const msg = String(error?.message || '');
  if (error?.name === 'TypeError' && (/fetch/i.test(msg) || /network/i.test(msg) || msg === 'Failed to fetch')) {
    return new Error(CONNECTION_ERROR_MESSAGE);
  }
  return error instanceof Error ? error : new Error(msg || CONNECTION_ERROR_MESSAGE);
};

const readErrorMessage = async (response) => {
  const contentType = response.headers.get('content-type') || '';
  let raw = '';
  if (contentType.includes('application/json')) {
    const data = await response.json().catch(() => ({}));
    raw = data.message || data.error || '';
    if (Array.isArray(data.details) && data.details.length) {
      const detailText = data.details.map((item) => item.msg || item.message).filter(Boolean).join(' — ');
      if (detailText) {
        raw = detailText;
      }
    }
  } else {
    raw = await response.text().catch(() => '');
  }
  if (isPayloadTooLarge(response.status, raw)) {
    return UPLOAD_LIMIT_MESSAGE;
  }
  if (isSessionError(response.status, raw)) {
    return SESSION_EXPIRED_MESSAGE;
  }
  return raw || `HTTP error! status: ${response.status}`;
};

const throwIfNotOk = async (response, headers = {}) => {
  const status = response.status;
  if (status >= 200 && status < 300) {
    return;
  }
  const authHeader = headers.Authorization || headers.authorization || '';
  const message = await readErrorMessage(response);
  if (authHeader && (status === 401 || status === 403)) {
    redirectToLogin();
  }
  throw new Error(message);
};

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      await throwIfNotOk(response, config.headers);
      
      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        throw new Error('Expected JSON response but received non-JSON content');
      }
    } catch (error) {
      console.error('API request failed:', error);
      throw asApiError(error);
    }
  }

  // GET request
  async get(endpoint, params = {}, customHeaders = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    return this.request(url, {
      method: 'GET',
      headers: customHeaders,
    });
  }

  // POST request
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT request
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  // POST with FormData (for file uploads)
  async postFormData(endpoint, formData, customHeaders = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: customHeaders,
        body: formData,
      });
      await throwIfNotOk(response, customHeaders);
      
      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        throw new Error('Expected JSON response but received non-JSON content');
      }
    } catch (error) {
      console.error('API request failed:', error);
      throw asApiError(error);
    }
  }

  postFormDataWithProgress(endpoint, formData, customHeaders = {}, onProgress) {
    const url = `${this.baseURL}${endpoint}`;
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url);
      Object.entries(customHeaders || {}).forEach(([key, value]) => {
        if (value) xhr.setRequestHeader(key, value);
      });
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && typeof onProgress === 'function') {
          onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = async () => {
        try {
          const response = {
            status: xhr.status,
            headers: { get: (name) => xhr.getResponseHeader(name) },
            json: async () => JSON.parse(xhr.responseText || '{}'),
            text: async () => xhr.responseText || ''
          };
          await throwIfNotOk(response, customHeaders);
          const contentType = xhr.getResponseHeader('content-type') || '';
          if (contentType.includes('application/json')) {
            resolve(JSON.parse(xhr.responseText || '{}'));
            return;
          }
          throw new Error('Expected JSON response but received non-JSON content');
        } catch (error) {
          reject(error);
        }
      };
      xhr.onerror = () => reject(new Error('تعذر الاتصال بالخادم أثناء الرفع'));
      xhr.send(formData);
    });
  }

  // PUT with FormData (for file uploads)
  async putFormData(endpoint, formData, customHeaders = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    try {
      const response = await fetch(url, {
        method: 'PUT',
        headers: customHeaders,
        body: formData,
      });
      await throwIfNotOk(response, customHeaders);
      
      // Check if response is JSON before parsing
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        return await response.json();
      } else {
        throw new Error('Expected JSON response but received non-JSON content');
      }
    } catch (error) {
      console.error('API request failed:', error);
      throw asApiError(error);
    }
  }
}

// Import mock API if needed
let mockMartyrsApi, mockAuthApi, mockAdminApi;
if (USE_MOCK_API) {
  const mockApi = require('./mockApi');
  mockMartyrsApi = mockApi.mockMartyrsApi;
  mockAuthApi = mockApi.mockAuthApi;
  mockAdminApi = mockApi.mockAdminApi;
}

// Martyrs API methods
export const martyrsApi = {
  // Get all martyrs with pagination and filters
  getAll: (params = {}) => {
    if (USE_MOCK_API) return mockMartyrsApi.getAll(params);
    const api = new ApiService();
    return api.get('/martyrs', params);
  },

  // Get a specific martyr by ID
  getById: (id) => {
    if (USE_MOCK_API) return mockMartyrsApi.getById(id);
    const api = new ApiService();
    return api.get(`/martyrs/${id}`);
  },

  // Get statistics
  getStats: () => {
    if (USE_MOCK_API) return mockMartyrsApi.getStats();
    const api = new ApiService();
    return api.get('/stats/overview');
  },

  // Get martyrs count
  getCount: () => {
    if (USE_MOCK_API) return mockMartyrsApi.getCount();
    const api = new ApiService();
    return api.get('/martyrs/count');
  },

  getOptions: (token) => {
    const api = new ApiService();
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    return api.get('/martyrs/options', {}, headers);
  },

  // Add a new martyr (public endpoint)
  addPublic: (formData) => {
    if (USE_MOCK_API) return mockMartyrsApi.addPublic(formData);
    const api = new ApiService();
    return api.postFormData('/martyrs/public', formData);
  },

  // Add a new martyr (admin endpoint)
  add: (formData) => {
    if (USE_MOCK_API) return mockMartyrsApi.add(formData);
    const api = new ApiService();
    return api.postFormData('/martyrs', formData);
  },

  // Update a martyr
  update: (id, formData) => {
    if (USE_MOCK_API) return mockMartyrsApi.update(id, formData);
    const api = new ApiService();
    return api.postFormData(`/martyrs/${id}`, formData);
  },

  // Delete a martyr
  delete: (id) => {
    if (USE_MOCK_API) return mockMartyrsApi.delete(id);
    const api = new ApiService();
    return api.delete(`/martyrs/${id}`);
  },

  // Get all martyrs for admin (including unapproved)
  getAllForAdmin: (params = {}) => {
    if (USE_MOCK_API) return mockMartyrsApi.getAllForAdmin(params);
    const api = new ApiService();
    return api.get('/martyrs/admin/all', params);
  },

  // Approve/unapprove a martyr
  approve: (id, approved) => {
    if (USE_MOCK_API) return mockMartyrsApi.approve(id, approved);
    const api = new ApiService();
    return api.request(`/martyrs/${id}/approve`, {
      method: 'PATCH',
      body: JSON.stringify({ approved })
    });
  },
};

// Auth API methods
export const authApi = {
  login: (credentials) => {
    if (USE_MOCK_API) return mockAuthApi.login(credentials);
    const api = new ApiService();
    return api.post('/auth/login', credentials);
  },
};

// Admin API methods
export const adminApi = {
  getAllMartyrs: (params = {}, token) => {
    if (USE_MOCK_API) return mockAdminApi.getAllMartyrs(params, token);
    const api = new ApiService();
    return api.get('/martyrs/admin/all', params, { 'Authorization': `Bearer ${token}` });
  },
  
  approveMartyr: (id, approved, token, status = null) => {
    if (USE_MOCK_API) return mockAdminApi.approveMartyr(id, approved, token);
    const api = new ApiService();
    
    // Prepare the request body
    const body = { 
      approved: approved
    };
    
    // Add status if provided
    if (status) {
      body.status = status;
    }
    
    
    return api.request(`/martyrs/${id}/approve`, {
      method: 'PATCH',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    });
  },
  
  deleteMartyr: (id, token) => {
    if (USE_MOCK_API) return mockAdminApi.deleteMartyr(id, token);
    const api = new ApiService();
    return api.request(`/martyrs/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
  },
  
  getMartyrById: (id, token) => {
    if (USE_MOCK_API) return mockAdminApi.getMartyrById(id, token);
    const api = new ApiService();
    return api.get(`/martyrs/${id}`, {}, { 'Authorization': `Bearer ${token}` });
  },
  
  updateMartyr: (id, formData, token) => {
    if (USE_MOCK_API) return mockAdminApi.updateMartyr(id, formData, token);
    const api = new ApiService();
    
    // Use direct fetch for FormData with PUT method
    return api.putFormData(`/martyrs/${id}`, formData, { 
      'Authorization': `Bearer ${token}`
    });
  },
  
  addMartyr: (formData, token) => {
    if (USE_MOCK_API) return mockAdminApi.addMartyr(formData, token);
    const api = new ApiService();
    return api.postFormData('/martyrs', formData, { 'Authorization': `Bearer ${token}` });
  }
};

const authHeaders = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

export const writingsApi = {
  getAll: (params = {}, token) => {
    const api = new ApiService();
    return api.get('/writings', params, authHeaders(token));
  },
  getById: (id) => {
    const api = new ApiService();
    return api.get(`/writings/${id}`);
  },
  create: (formData, token) => {
    const api = new ApiService();
    return api.postFormData('/writings', formData, authHeaders(token));
  },
  update: (id, formData, token) => {
    const api = new ApiService();
    return api.putFormData(`/writings/${id}`, formData, authHeaders(token));
  },
  delete: (id, token) => {
    const api = new ApiService();
    return api.request(`/writings/${id}`, {
      method: 'DELETE',
      headers: authHeaders(token)
    });
  }
};

export const poemsApi = {
  getAll: (params = {}, token) => {
    const api = new ApiService();
    return api.get('/poems', params, authHeaders(token));
  },
  getById: (id) => {
    const api = new ApiService();
    return api.get(`/poems/${id}`);
  },
  create: (data, token) => {
    const api = new ApiService();
    return api.request('/poems', {
      method: 'POST',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  },
  update: (id, data, token) => {
    const api = new ApiService();
    return api.request(`/poems/${id}`, {
      method: 'PUT',
      headers: { ...authHeaders(token), 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    });
  },
  delete: (id, token) => {
    const api = new ApiService();
    return api.request(`/poems/${id}`, {
      method: 'DELETE',
      headers: authHeaders(token)
    });
  }
};

export const mediaApi = {
  getAll: (params = {}, token) => {
    const api = new ApiService();
    return api.get('/media', params, authHeaders(token));
  },
  create: (formData, token, onProgress) => {
    const api = new ApiService();
    const headers = authHeaders(token);
    if (typeof onProgress === 'function') {
      return api.postFormDataWithProgress('/media', formData, headers, onProgress);
    }
    return api.postFormData('/media', formData, headers);
  },
  update: (id, formData, token) => {
    const api = new ApiService();
    return api.putFormData(`/media/${id}`, formData, authHeaders(token));
  },
  delete: (id, token) => {
    const api = new ApiService();
    return api.request(`/media/${id}`, {
      method: 'DELETE',
      headers: authHeaders(token)
    });
  }
};

export const tributesApi = {
  getForMartyr: (martyrId, params = {}) => {
    const api = new ApiService();
    return api.get(`/tributes/martyr/${martyrId}`, params);
  },
  getRecent: (params = {}) => {
    const api = new ApiService();
    return api.get('/tributes/recent', params);
  },
  submit: (data) => {
    const api = new ApiService();
    return api.post('/tributes', data);
  },
  getPending: (params = {}, token) => {
    const api = new ApiService();
    return api.get('/tributes/pending', params, authHeaders(token));
  },
  approve: (id, token) => {
    const api = new ApiService();
    return api.request(`/tributes/${id}/approve`, {
      method: 'PATCH',
      headers: authHeaders(token)
    });
  },
  reject: (id, token) => {
    const api = new ApiService();
    return api.request(`/tributes/${id}`, {
      method: 'DELETE',
      headers: authHeaders(token)
    });
  }
};

export const WRITING_TYPE_LABELS = {
  book: 'كتاب',
  article: 'مقال',
  essay: 'خاطرة',
  other: 'أخرى'
};

// Health check
export const healthApi = {
  check: () => {
    const api = new ApiService();
    return api.get('/health');
  },
};

export const contactApi = {
  send: (data) => {
    const api = new ApiService();
    return api.post('/contact', data);
  }
};

export default ApiService;
